import { useAuth } from '../lib/auth-context';
import { getRecentActivity } from '../lib/api';
import { getMcpEndpoint } from '../lib/mcp';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import useSWR from 'swr';

type RecentActivity = {
  id: number;
  activity_type: string;
  event_source: string;
  created_at: string;
};

async function fetchRecentActivity(
  token: string,
): Promise<{ activities: RecentActivity[]; recentActivityUnavailable: boolean }> {
  try {
    const response = await getRecentActivity(token, 5);
    const payload = (await response.json().catch(() => null)) as { activities?: RecentActivity[] } | null;

    if (!response.ok) {
      return { activities: [], recentActivityUnavailable: true };
    }

    return {
      activities: payload?.activities ?? [],
      recentActivityUnavailable: false,
    };
  } catch {
    return { activities: [], recentActivityUnavailable: true };
  }
}

async function fetchTenantData(tenantId: string, token: string) {
  const [tenantRes, activityRes] = await Promise.all([
    supabase.from('tenants').select('*').eq('tenant_id', tenantId).single(),
    fetchRecentActivity(token),
  ]);

  if (tenantRes.error) {
    throw new Error(tenantRes.error.message);
  }

  return {
    tenant: tenantRes.data,
    activities: activityRes.activities,
    recentActivityUnavailable: activityRes.recentActivityUnavailable,
  };
}

export function OverviewPage() {
  const { user, session } = useAuth();
  const mcpEndpoint = getMcpEndpoint();
  const [dismissBanner, setDismissBanner] = useState(false);
  const { data, error } = useSWR(
    user && session ? ['overview', user.id] : null,
    () => fetchTenantData(user!.id, session!.access_token),
  );

  if (error) return <div className="text-red-600">Failed to load dashboard data.</div>;
  if (!data) return <div className="text-gray-500">Loading...</div>;

  const hasActivity = data.activities && data.activities.length > 0;
  const showBanner = !dismissBanner && !hasActivity && !data.recentActivityUnavailable;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Dashboard</h2>

      {showBanner && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">
              <span className="font-semibold">Get started</span> — Connect GxP Sentinel to your AI tools in 2 minutes
            </p>
          </div>
          <div className="flex gap-2 ml-4">
            <Link
              to="/welcome"
              className="text-xs font-medium text-blue-700 hover:text-blue-900 px-3 py-1 rounded hover:bg-blue-100"
            >
              Set up now
            </Link>
            <button
              onClick={() => setDismissBanner(true)}
              className="text-xs text-blue-600 hover:text-blue-800 px-2"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">Subscription Tier</p>
          <p className="text-2xl font-semibold capitalize">{data.tenant?.subscription_tier ?? 'Explorer'}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">Rate Limit (RPM)</p>
          <p className="text-2xl font-semibold">{data.tenant?.rate_limit_rpm ?? 10}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">Daily Limit</p>
          <p className="text-2xl font-semibold">{data.tenant?.rate_limit_daily ?? 100}</p>
        </div>
      </div>

      <div className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <h3 className="text-lg font-semibold text-slate-900">Use GxP Sentinel through MCP</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Register an OAuth client, implement the PKCE flow to get an access token, and connect your MCP-compatible client to the endpoint below.
            </p>
          </div>
          <Link
            to="/mcp-setup"
            className="inline-flex items-center justify-center rounded-md bg-sentinel-700 px-4 py-2 text-sm font-medium text-white hover:bg-sentinel-900"
          >
            Open setup guide
          </Link>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step 1</p>
            <p className="mt-2 text-sm font-medium text-slate-900">Register an OAuth client</p>
            <p className="mt-2 text-sm leading-6 text-slate-600">Register a new OAuth client to get a client_id and registration access token.</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step 2</p>
            <p className="mt-2 text-sm font-medium text-slate-900">Set your MCP endpoint</p>
            <code className="mt-2 block break-all text-xs text-slate-900">{mcpEndpoint}</code>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step 3</p>
            <p className="mt-2 text-sm font-medium text-slate-900">Confirm traffic in the dashboard</p>
            <p className="mt-2 text-sm leading-6 text-slate-600">After your first tool call, Usage and Recent Activity should start moving.</p>
          </div>
        </div>
      </div>

      <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
      {data.recentActivityUnavailable ? (
        <p className="mb-4 text-sm text-amber-700">Recent activity is temporarily unavailable.</p>
      ) : null}
      {data.activities.length === 0 ? (
        <p className="text-gray-500">No recent activity.</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-gray-500">Activity</th>
                <th className="text-left px-4 py-3 text-gray-500">Source</th>
                <th className="text-left px-4 py-3 text-gray-500">Time</th>
              </tr>
            </thead>
            <tbody>
              {data.activities.map((activity: RecentActivity) => (
                <tr key={activity.id} className="border-t">
                  <td className="px-4 py-3">{activity.activity_type}</td>
                  <td className="px-4 py-3">{activity.event_source}</td>
                  <td className="px-4 py-3 text-gray-500">{new Date(activity.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}